import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Box,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Switch,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
} from "@mui/material";
import {
  Description as DescriptionIcon,
  Print as PrintIcon,
  Receipt as ReceiptIcon,
  Schedule as ScheduleIcon,
  AddTask as AddTaskIcon,
  Paid as PaidIcon,
  Undo as UndoIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
} from "@mui/icons-material";

const ExpenseReportGenerator: React.FC = () => {
  const API_BASE_URL =
    process.env.REACT_APP_API_URL || "http://localhost:3001/api";

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  const [month, setMonth] = useState<number>(currentMonth);
  const [year, setYear] = useState<number>(currentYear);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [categoryStats, setCategoryStats] = useState<{
    despesa: { total: number; valor: number };
    despesa_a_faturar: { total: number; valor: number };
    deferred: { total: number; valor: number };
    incoming: { total: number; valor: number };
  }>({
    despesa: { total: 0, valor: 0 },
    despesa_a_faturar: { total: 0, valor: 0 },
    deferred: { total: 0, valor: 0 },
    incoming: { total: 0, valor: 0 },
  });

  type Candidate = {
    id: number;
    data_emissao: string;
    valor: number;
    tipo: string | null;
    descricao: string | null;
    categoria: string;
    mes_relatorio: number | null;
  };

  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [candidatesValor, setCandidatesValor] = useState<number>(0);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [allMonths, setAllMonths] = useState(false);
  const [includeLoading, setIncludeLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<{
    total: number;
    pagas: number;
    nao_pagas: number;
  }>({ total: 0, pagas: 0, nao_pagas: 0 });
  const [markLoading, setMarkLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<"pay" | "unpay">("pay");
  const [sortBy, setSortBy] = useState<"data" | "valor">("data");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const months = [
    { value: 1, label: "Janeiro" },
    { value: 2, label: "Fevereiro" },
    { value: 3, label: "Março" },
    { value: 4, label: "Abril" },
    { value: 5, label: "Maio" },
    { value: 6, label: "Junho" },
    { value: 7, label: "Julho" },
    { value: 8, label: "Agosto" },
    { value: 9, label: "Setembro" },
    { value: 10, label: "Outubro" },
    { value: 11, label: "Novembro" },
    { value: 12, label: "Dezembro" },
  ];

  const years = Array.from({ length: 6 }, (_, i) => currentYear - i);

  // Helper function to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  };

  // Load stats when month/year changes
  const loadStats = React.useCallback(async () => {
    try {
      setStatsLoading(true);

      // Load regular stats
      // Use 'a_transferir_view' to include both despesa + despesa_a_faturar
      const response = await fetch(
        `${API_BASE_URL}/invoices/summary/stats?mes=${month}&ano=${year}&categoria=a_transferir_view`,
        { headers: getAuthHeaders() }
      );
      const data = await response.json();

      const newStats = {
        despesa: { total: 0, valor: 0 },
        despesa_a_faturar: { total: 0, valor: 0 },
        deferred: { total: 0, valor: 0 },
        incoming: { total: 0, valor: 0 },
      };

      if (data.success && data.data.length > 0) {
        // Backend returns separate rows for each category
        // Note: valor_total comes as string from MySQL, so we need to parse it
        data.data.forEach((row: any) => {
          if (row.categoria === "despesa") {
            newStats.despesa = {
              total: row.total_faturas || 0,
              valor: parseFloat(row.valor_total || 0),
            };
          } else if (row.categoria === "despesa_a_faturar") {
            newStats.despesa_a_faturar = {
              total: row.total_faturas || 0,
              valor: parseFloat(row.valor_total || 0),
            };
          }
        });
      }

      // Load deferred stats (invoices postponed to future months)
      const deferredResponse = await fetch(
        `${API_BASE_URL}/invoices/summary/deferred?mes=${month}&ano=${year}&categoria=a_transferir_view`,
        { headers: getAuthHeaders() }
      );
      const deferredData = await deferredResponse.json();

      if (deferredData.success && deferredData.data) {
        newStats.deferred = {
          total: deferredData.data.total_deferred || 0,
          valor: parseFloat(deferredData.data.valor_total_deferred || 0),
        };
      }

      // Load incoming deferred stats (invoices from previous months deferred to current month)
      const incomingResponse = await fetch(
        `${API_BASE_URL}/invoices/summary/incoming-deferred?mes=${month}&ano=${year}&categoria=a_transferir_view`,
        { headers: getAuthHeaders() }
      );
      const incomingData = await incomingResponse.json();

      if (incomingData.success && incomingData.data.length > 0) {
        // Sum up all incoming deferred by category
        incomingData.data.forEach((row: any) => {
          newStats.incoming.total += row.total_faturas || 0;
          newStats.incoming.valor += parseFloat(row.valor_total || 0);
        });
      }

      setCategoryStats(newStats);
    } catch (error) {
      console.error("Error loading stats:", error);
      setCategoryStats({
        despesa: { total: 0, valor: 0 },
        despesa_a_faturar: { total: 0, valor: 0 },
        deferred: { total: 0, valor: 0 },
        incoming: { total: 0, valor: 0 },
      });
    } finally {
      setStatsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, year]);

  useEffect(() => {
    loadStats();
    const handler = () => loadStats();
    window.addEventListener("expense-stats-refresh", handler);
    return () => window.removeEventListener("expense-stats-refresh", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, year]);

  const loadCandidates = React.useCallback(async () => {
    try {
      setCandidatesLoading(true);
      const response = await fetch(
        `${API_BASE_URL}/invoices/reports/unpaid-candidates?mes=${month}&ano=${year}&all_months=${allMonths}`,
        { headers: getAuthHeaders() }
      );
      const data = await response.json();
      if (data.success) {
        setCandidates(data.data || []);
        setCandidatesValor(Number(data.summary?.valor) || 0);
      } else {
        setCandidates([]);
        setCandidatesValor(0);
      }
      setSelectedIds(new Set());
    } catch (e) {
      console.error("Error loading candidates:", e);
      setCandidates([]);
      setCandidatesValor(0);
    } finally {
      setCandidatesLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, year, allMonths]);

  useEffect(() => {
    loadCandidates();
  }, [loadCandidates]);

  const loadPaymentStatus = React.useCallback(async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/invoices/reports/payment-status?mes=${month}&ano=${year}`,
        { headers: getAuthHeaders() }
      );
      const data = await response.json();
      if (data.success && data.data) {
        setPaymentStatus({
          total: Number(data.data.total) || 0,
          pagas: Number(data.data.pagas) || 0,
          nao_pagas: Number(data.data.nao_pagas) || 0,
        });
      }
    } catch (e) {
      console.error("Error loading payment status:", e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, year]);

  useEffect(() => {
    loadPaymentStatus();
    const handler = () => loadPaymentStatus();
    window.addEventListener("expense-stats-refresh", handler);
    return () => window.removeEventListener("expense-stats-refresh", handler);
  }, [loadPaymentStatus]);

  const openConfirm = (action: "pay" | "unpay") => {
    setConfirmAction(action);
    setConfirmOpen(true);
  };

  const handleMarkPaid = async () => {
    setMarkLoading(true);
    setError(null);
    setConfirmOpen(false);
    try {
      const response = await fetch(`${API_BASE_URL}/invoices/reports/mark-paid`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          mes: month,
          ano: year,
          pago: confirmAction === "pay",
        }),
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.error || "Erro ao atualizar pagamento");
      await loadPaymentStatus();
      await loadCandidates();
      window.dispatchEvent(new Event("expense-stats-refresh"));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao atualizar pagamento");
    } finally {
      setMarkLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    setLoading(true);
    setError(null);

    try {
      // Use API_BASE_URL for environment-aware requests
      const response = await fetch(
        `${API_BASE_URL}/invoices/reports/expense?mes=${month}&ano=${year}`,
        { headers: getAuthHeaders() }
      );

      if (!response.ok) {
        // Try to get error message
        let errorMessage = "Erro ao gerar relatório";
        try {
          const contentType = response.headers.get("content-type");
          if (contentType && contentType.includes("application/json")) {
            const data = await response.json();
            errorMessage = data.error || errorMessage;
          } else {
            const text = await response.text();
            errorMessage = text || errorMessage;
          }
        } catch (e) {
          // Ignore parsing errors
        }
        throw new Error(errorMessage);
      }

      // Get the PDF as blob
      const blob = await response.blob();

      // Verify it's a PDF
      if (blob.type !== "application/pdf") {
        throw new Error("Resposta inválida do servidor (não é um PDF)");
      }

      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob);

      // Open in new window
      const newWindow = window.open(url, "_blank");

      if (newWindow) {
        // Wait a bit for the window to load, then trigger print
        setTimeout(() => {
          newWindow.print();
        }, 1000);

        // Don't revoke URL - let the browser keep it
        // The browser will clean it up when the tab is closed
      } else {
        // If popup was blocked, show error
        window.URL.revokeObjectURL(url);
        throw new Error(
          "Pop-up bloqueado. Por favor, permita pop-ups para este site."
        );
      }
    } catch (err) {
      console.error("Error generating report:", err);
      setError(err instanceof Error ? err.message : "Erro ao gerar relatório");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReport = async () => {
    setLoading(true);
    setError(null);

    try {
      // Use API_BASE_URL for environment-aware requests
      const response = await fetch(
        `${API_BASE_URL}/invoices/reports/expense?mes=${month}&ano=${year}&download=true`,
        { headers: getAuthHeaders() }
      );

      if (!response.ok) {
        // Try to get error message
        let errorMessage = "Erro ao baixar relatório";
        try {
          const contentType = response.headers.get("content-type");
          if (contentType && contentType.includes("application/json")) {
            const data = await response.json();
            errorMessage = data.error || errorMessage;
          } else {
            const text = await response.text();
            errorMessage = text || errorMessage;
          }
        } catch (e) {
          // Ignore parsing errors
        }
        throw new Error(errorMessage);
      }

      // Get the PDF as blob
      const blob = await response.blob();

      // Verify it's a PDF
      if (blob.type !== "application/pdf") {
        throw new Error("Resposta inválida do servidor (não é um PDF)");
      }

      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `folha-despesas-${year}-${String(month).padStart(
        2,
        "0"
      )}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      // Clean up after a delay
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (err) {
      console.error("Error downloading report:", err);
      setError(err instanceof Error ? err.message : "Erro ao baixar relatório");
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleIncludeSelected = async () => {
    if (selectedIds.size === 0) return;
    setIncludeLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/invoices/reports/include`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ ids: Array.from(selectedIds), mes: month, ano: year }),
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.error || "Erro ao incluir faturas");
      const requested = selectedIds.size;
      const affected = Number(data.affected) || 0;
      await loadCandidates();
      window.dispatchEvent(new Event("expense-stats-refresh"));
      if (affected < requested) {
        setError(
          `${affected} de ${requested} fatura(s) incluída(s). As restantes não puderam ser incluídas — faturas de outro ano não podem ser puxadas para esta folha.`
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao incluir faturas");
    } finally {
      setIncludeLoading(false);
    }
  };

  const sortedCandidates = [...candidates].sort((a, b) => {
    const cmp =
      sortBy === "valor"
        ? (Number(a.valor) || 0) - (Number(b.valor) || 0)
        : new Date(a.data_emissao).getTime() - new Date(b.data_emissao).getTime();
    return sortDir === "asc" ? cmp : -cmp;
  });

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        <DescriptionIcon sx={{ verticalAlign: "middle", mr: 1 }} />
        Folha de Despesas
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Gere relatórios de despesas mensais para impressão
      </Typography>

      {/* Statistics Summary for selected period (expenses by category) */}
      {statsLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ mb: 3 }}>
          {/* Total Geral em destaque */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, opacity: 0.9 }}>
              Total Geral do Mês
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
              <Box sx={{ flex: "1 1 150px", textAlign: "center" }}>
                <Typography variant="h4">
                  {categoryStats.despesa.total +
                    categoryStats.despesa_a_faturar.total -
                    categoryStats.deferred.total +
                    categoryStats.incoming.total}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Total de Faturas
                </Typography>
              </Box>
              <Box sx={{ flex: "1 1 150px", textAlign: "center" }}>
                <Typography variant="h4">
                  €
                  {(
                    categoryStats.despesa.valor +
                    categoryStats.despesa_a_faturar.valor -
                    categoryStats.deferred.valor +
                    categoryStats.incoming.valor
                  ).toFixed(2)}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Valor Total
                </Typography>
              </Box>
              <Box sx={{ flex: "1 1 150px", textAlign: "center" }}>
                <Typography variant="h4">
                  €
                  {(categoryStats.despesa.total +
                    categoryStats.despesa_a_faturar.total -
                    categoryStats.deferred.total +
                    categoryStats.incoming.total >
                  0
                    ? (categoryStats.despesa.valor +
                        categoryStats.despesa_a_faturar.valor -
                        categoryStats.deferred.valor +
                        categoryStats.incoming.valor) /
                      (categoryStats.despesa.total +
                        categoryStats.despesa_a_faturar.total -
                        categoryStats.deferred.total +
                        categoryStats.incoming.total)
                    : 0
                  ).toFixed(2)}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Valor Médio
                </Typography>
              </Box>
              {categoryStats.deferred.total > 0 && (
                <Box sx={{ flex: "1 1 150px", textAlign: "center" }}>
                  <Typography variant="h4" sx={{ color: "warning.light" }}>
                    -{categoryStats.deferred.total}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Adiadas (-€{categoryStats.deferred.valor.toFixed(2)})
                  </Typography>
                </Box>
              )}
              {categoryStats.incoming.total > 0 && (
                <Box sx={{ flex: "1 1 150px", textAlign: "center" }}>
                  <Typography variant="h4" sx={{ color: "info.light" }}>
                    +{categoryStats.incoming.total}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    De Meses Anteriores (+€
                    {categoryStats.incoming.valor.toFixed(2)})
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>

          {/* Breakdown por categoria */}
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Por Categoria
          </Typography>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            {/* Despesa */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Card variant="outlined">
                <CardContent>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    <ReceiptIcon color="primary" fontSize="small" />
                    <Typography variant="subtitle2" color="text.secondary">
                      Despesas
                    </Typography>
                  </Stack>
                  <Typography variant="h6">
                    {categoryStats.despesa.total}
                  </Typography>
                  <Typography variant="body2" color="primary">
                    €{categoryStats.despesa.valor.toFixed(2)}
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            {/* Despesa A Faturar */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Card variant="outlined">
                <CardContent>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    <ReceiptIcon color="secondary" fontSize="small" />
                    <Typography variant="subtitle2" color="text.secondary">
                      Despesas A Faturar
                    </Typography>
                  </Stack>
                  <Typography variant="h6">
                    {categoryStats.despesa_a_faturar.total}
                  </Typography>
                  <Typography variant="body2" color="secondary">
                    €{categoryStats.despesa_a_faturar.valor.toFixed(2)}
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            {/* Despesas Adiadas para meses futuros */}
            {categoryStats.deferred.total > 0 && (
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Card
                  variant="outlined"
                  sx={{
                    bgcolor: "warning.light",
                    color: "warning.contrastText",
                  }}
                >
                  <CardContent>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ mb: 1 }}
                    >
                      <ScheduleIcon fontSize="small" />
                      <Typography variant="subtitle2">
                        Adiadas (não contam)
                      </Typography>
                    </Stack>
                    <Typography variant="h6">
                      {categoryStats.deferred.total}
                    </Typography>
                    <Typography variant="body2">
                      €{categoryStats.deferred.valor.toFixed(2)}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ mt: 0.5, display: "block", opacity: 0.8 }}
                    >
                      Emitidas em {months.find((m) => m.value === month)?.label}{" "}
                      mas adiadas
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            )}

            {/* Faturas Adiadas de meses anteriores (agora contam) */}
            {categoryStats.incoming.total > 0 && (
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Card
                  variant="outlined"
                  sx={{ bgcolor: "info.light", color: "info.contrastText" }}
                >
                  <CardContent>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ mb: 1 }}
                    >
                      <ScheduleIcon fontSize="small" />
                      <Typography variant="subtitle2">
                        Adiadas de Meses Anteriores
                      </Typography>
                    </Stack>
                    <Typography variant="h6">
                      {categoryStats.incoming.total}
                    </Typography>
                    <Typography variant="body2">
                      €{categoryStats.incoming.valor.toFixed(2)}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ mt: 0.5, display: "block", opacity: 0.8 }}
                    >
                      Emitidas antes mas adiadas para{" "}
                      {months.find((m) => m.value === month)?.label}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            )}
          </Stack>
        </Box>
      )}

      {/* Faturas em atraso (por incluir na folha) */}
      <Divider sx={{ my: 2 }} />
      <Box sx={{ mb: 3 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1}
          sx={{ mb: 1 }}
        >
          <Typography variant="h6">
            Faturas em atraso{" "}
            <Chip
              size="small"
              label={`${candidates.length} · €${candidatesValor.toFixed(2)}`}
              color={candidates.length > 0 ? "warning" : "default"}
            />
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={allMonths}
                onChange={(e) => setAllMonths(e.target.checked)}
              />
            }
            label="Mostrar todos os meses"
          />
        </Stack>

        {candidatesLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : candidates.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Sem faturas em atraso para incluir nesta folha.
          </Typography>
        ) : (
          <>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Typography variant="caption" color="text.secondary">
                Ordenar:
              </Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={sortBy}
                onChange={(_, v) => v && setSortBy(v)}
              >
                <ToggleButton value="data">Data</ToggleButton>
                <ToggleButton value="valor">Valor</ToggleButton>
              </ToggleButtonGroup>
              <IconButton
                size="small"
                onClick={() =>
                  setSortDir((d) => (d === "asc" ? "desc" : "asc"))
                }
                aria-label="Inverter direção da ordenação"
              >
                {sortDir === "asc" ? (
                  <ArrowUpwardIcon fontSize="small" />
                ) : (
                  <ArrowDownwardIcon fontSize="small" />
                )}
              </IconButton>
            </Stack>
            <Paper variant="outlined" sx={{ maxHeight: 280, overflow: "auto" }}>
              <List dense disablePadding>
                {sortedCandidates.map((c) => {
                  const date = new Date(c.data_emissao);
                  const dateStr = `${String(date.getDate()).padStart(2, "0")}/${String(
                    date.getMonth() + 1
                  ).padStart(2, "0")}/${date.getFullYear()}`;
                  const label = c.descricao || c.tipo || "Despesa";
                  return (
                    <ListItem
                      key={c.id}
                      onClick={() => toggleSelect(c.id)}
                      sx={{ cursor: "pointer" }}
                      secondaryAction={
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          €{(Number(c.valor) || 0).toFixed(2)}
                        </Typography>
                      }
                    >
                      <Checkbox
                        edge="start"
                        checked={selectedIds.has(c.id)}
                        tabIndex={-1}
                        disableRipple
                      />
                      <ListItemText
                        primary={label}
                        secondary={`${dateStr} · ${c.categoria}`}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </Paper>
            <Button
              variant="contained"
              color="secondary"
              startIcon={
                includeLoading ? <CircularProgress size={20} /> : <AddTaskIcon />
              }
              onClick={handleIncludeSelected}
              disabled={selectedIds.size === 0 || includeLoading}
              sx={{ mt: 1.5 }}
            >
              Incluir {selectedIds.size > 0 ? `${selectedIds.size} ` : ""}
              selecionada(s) nesta folha
            </Button>
          </>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems="stretch"
      >
        <FormControl sx={{ flex: 1, minWidth: 120 }}>
          <InputLabel>Mês</InputLabel>
          <Select
            value={month}
            label="Mês"
            onChange={(e) => setMonth(e.target.value as number)}
            disabled={loading}
          >
            {months.map((m) => (
              <MenuItem key={m.value} value={m.value}>
                {m.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ flex: 1, minWidth: 120 }}>
          <InputLabel>Ano</InputLabel>
          <Select
            value={year}
            label="Ano"
            onChange={(e) => setYear(e.target.value as number)}
            disabled={loading}
          >
            {years.map((y) => (
              <MenuItem key={y} value={y}>
                {y}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Stack direction="row" spacing={1} sx={{ flex: 1 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleGenerateReport}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <PrintIcon />}
            fullWidth
          >
            {loading ? "Gerando..." : "Imprimir"}
          </Button>

          <Button
            variant="outlined"
            color="primary"
            onClick={handleDownloadReport}
            disabled={loading}
            startIcon={
              loading ? <CircularProgress size={20} /> : <DescriptionIcon />
            }
          >
            Baixar
          </Button>
        </Stack>
      </Stack>

      {/* Estado de pagamento da folha */}
      {paymentStatus.total > 0 && (
        <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <Chip
            icon={<PaidIcon />}
            label={
              paymentStatus.nao_pagas === 0
                ? "Folha paga"
                : `${paymentStatus.pagas}/${paymentStatus.total} pagas`
            }
            color={paymentStatus.nao_pagas === 0 ? "success" : "default"}
          />
          {paymentStatus.nao_pagas > 0 ? (
            <Button
              variant="contained"
              color="success"
              startIcon={markLoading ? <CircularProgress size={20} /> : <PaidIcon />}
              onClick={() => openConfirm("pay")}
              disabled={markLoading}
            >
              Marcar folha como paga
            </Button>
          ) : (
            <Button
              variant="outlined"
              color="warning"
              startIcon={markLoading ? <CircularProgress size={20} /> : <UndoIcon />}
              onClick={() => openConfirm("unpay")}
              disabled={markLoading}
            >
              Desmarcar como paga
            </Button>
          )}
        </Box>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>
          {confirmAction === "pay" ? "Marcar folha como paga?" : "Desmarcar como paga?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmAction === "pay"
              ? `Todas as despesas da folha de ${month}/${year} (${paymentStatus.total}) serão marcadas como pagas e deixarão de aparecer como faturas em atraso.`
              : `Todas as despesas da folha de ${month}/${year} voltarão ao estado não pago.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancelar</Button>
          <Button onClick={handleMarkPaid} variant="contained" autoFocus>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ExpenseReportGenerator;
